import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { format } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Badge } from '../../ui/badge'
import { Input } from '../../ui/input'
import Avatar from '../../leevo_ui/Avatar/Avatar'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'
import ConfirmationDialog from '../../leevo_ui/ConfirmationDialog/ConfirmationDialog'
import { requestCreator } from '../../../utilities/requests'
import { cn } from '../../../lib/utils'
import toast from '../../../utilities/toast'
import { hasDistinctStudentLegalName, studentDisplayName } from '../../../utilities/studentNames'

const { get: getWaitlist, post: postWaitlistAction, delete: removeStudent } = requestCreator()

function normalizeStudentSearchResult(student) {
  return {
    id: student.id,
    fullName: student.fullName || student.full_name,
    preferredName: student.preferredName || student.preferred_name,
    skillLevel: student.skillLevel || student.skill_level
  }
}

function deduplicateStudentsById(students) {
  return Array.from(new Map(students.map((student) => [student.id, student])).values())
}

function waitlistSkillLevelLabel(student) {
  if (!student) return ''
  const level = student.currentSkillLevel || student.current_skill_level
  return level?.name || ''
}

function WaitlistManagementSection({ klassId, facilityId, enrolledStudentIds = [] }) {
  const waitlistFetchSeq = useRef(0)
  const [waitlistEntries, setWaitlistEntries] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [studentResults, setStudentResults] = useState([])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [addError, setAddError] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const [removeDialogEntryId, setRemoveDialogEntryId] = useState(null)

  function messageFromWaitlistError(error) {
    const data = error.response?.data
    if (!data) return 'Something went wrong. Please try again.'
    if (Array.isArray(data.errors)) return data.errors.join(' ')
    if (typeof data.errors === 'string') return data.errors
    if (typeof data.error === 'string') return data.error
    return 'Something went wrong. Please try again.'
  }

  const loadWaitlist = useCallback(async () => {
    if (!klassId || !facilityId) return
    const seq = ++waitlistFetchSeq.current
    try {
      setIsLoading(true)
      const response = await getWaitlist(`/api/facilities/${facilityId}/klasses/${klassId}/waitlist_entries`, {
        params: {
          include: 'student,student.current_skill_level'
        }
      })
      if (seq !== waitlistFetchSeq.current) return
      setWaitlistEntries(Array.isArray(response) ? response : response?.records || [])
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error(messageFromWaitlistError(error))
      }
    } finally {
      if (seq === waitlistFetchSeq.current) {
        setIsLoading(false)
      }
    }
  }, [facilityId, klassId])

  useEffect(() => {
    loadWaitlist()
  }, [loadWaitlist])

  useEffect(() => {
    const query = searchTerm.trim()

    if (query.length < 2) {
      setStudentResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    const cancelSource = axios.CancelToken.source()
    const timeoutId = setTimeout(() => {
      axios
        .get(`/api/facilities/${facilityId}/klasses/${klassId}/waitlist_entries/eligible_students`, {
          params: { q: query },
          cancelToken: cancelSource.token
        })
        .then((response) =>
          setStudentResults(deduplicateStudentsById((response.data || []).map(normalizeStudentSearchResult)))
        )
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setStudentResults([])
          }
        })
        .finally(() => setIsSearching(false))
    }, 250)

    return () => {
      clearTimeout(timeoutId)
      cancelSource.cancel()
    }
  }, [facilityId, klassId, searchTerm])

  async function handlePromote(entryId) {
    setActionLoading(entryId)
    try {
      await postWaitlistAction(`/api/facilities/${facilityId}/waitlist_entries/${entryId}/promote`)
      await loadWaitlist()
      toast.success('Family notified.')
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error(messageFromWaitlistError(error))
      }
    } finally {
      setActionLoading(null)
    }
  }

  async function handleConfirmRemove() {
    if (!removeDialogEntryId) return
    setActionLoading(removeDialogEntryId)
    try {
      await removeStudent(`/api/facilities/${facilityId}/waitlist_entries/${removeDialogEntryId}`)
      await loadWaitlist()
      setRemoveDialogEntryId(null)
      toast.success('Removed from waitlist.')
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error(messageFromWaitlistError(error))
      }
    } finally {
      setActionLoading(null)
    }
  }

  async function handleAddStudentToWaitlist() {
    if (!selectedStudent) return
    setAddError(null)
    setIsAdding(true)

    try {
      await postWaitlistAction(`/api/facilities/${facilityId}/klasses/${klassId}/waitlist_entries`, {
        student_id: selectedStudent.id
      })
      setSearchTerm('')
      setStudentResults([])
      setSelectedStudent(null)
      await loadWaitlist()
      toast.success('Student added to waitlist.')
    } catch (error) {
      if (!axios.isCancel(error)) {
        const msg = messageFromWaitlistError(error)
        setAddError(msg)
        toast.error(msg)
      }
    } finally {
      setIsAdding(false)
    }
  }

  function getStatusBadge(status) {
    const variants = {
      active: 'secondary',
      notified: 'warning',
      moved_to_end: 'secondary',
      enrolled: 'success',
      cancelled: 'destructive'
    }
    return <Badge variant={variants[status] || 'default'}>{status.replace('_', ' ')}</Badge>
  }

  const activeWaitlistStudentIds = new Set(
    waitlistEntries.filter((entry) => entry.status !== 'cancelled').map((entry) => entry.student.id)
  )
  const enrolledStudentIdSet = new Set(enrolledStudentIds)

  const canAddSelectedStudent =
    selectedStudent &&
    !activeWaitlistStudentIds.has(selectedStudent.id) &&
    !enrolledStudentIdSet.has(selectedStudent.id)

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center p-8">
          <LoadingSpinner size="8" />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle>Waitlist ({waitlistEntries.length}/10)</CardTitle>
            </div>
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value)
                    setSelectedStudent(null)
                    setAddError(null)
                  }}
                  placeholder="Search students by name"
                  className="sm:max-w-sm"
                />
                <Button onClick={handleAddStudentToWaitlist} disabled={!canAddSelectedStudent || isAdding} size="sm">
                  {isAdding ? 'Adding...' : 'Add Student'}
                </Button>
              </div>
              {searchTerm.trim().length > 0 && searchTerm.trim().length < 2 ? (
                <p className="text-xs text-gray-500">Type at least 2 characters to search.</p>
              ) : null}
              {isSearching ? <p className="text-xs text-gray-500">Searching...</p> : null}
              {!isSearching && studentResults.length > 0 ? (
                <div className="max-h-44 overflow-y-auto rounded-md border border-gray-200 bg-white">
                  {studentResults.map((student) => {
                    const alreadyOnWaitlist = activeWaitlistStudentIds.has(student.id)
                    const alreadyInClass = enrolledStudentIdSet.has(student.id)
                    const isDisabled = alreadyOnWaitlist || alreadyInClass
                    const isSelected = selectedStudent?.id === student.id
                    return (
                      <button
                        type="button"
                        key={student.id}
                        disabled={isDisabled}
                        onClick={() => {
                          setSelectedStudent(student)
                          setAddError(null)
                        }}
                        className={cn(
                          'w-full border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0',
                          'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
                          isSelected ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'
                        )}
                      >
                        <div className="font-medium">{studentDisplayName(student)}</div>
                        {hasDistinctStudentLegalName(student) ? (
                          <div className="text-xs text-gray-500">{student.fullName}</div>
                        ) : null}
                        <div className="text-xs text-gray-500">
                          {student.skillLevel || 'No skill level'}
                          {alreadyInClass ? ' - Already in class' : ''}
                          {!alreadyInClass && alreadyOnWaitlist ? ' - Already on waitlist' : ''}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : null}
              {!isSearching && searchTerm.trim().length >= 2 && studentResults.length === 0 ? (
                <p className="text-xs text-gray-500">No students found.</p>
              ) : null}
              {selectedStudent ? (
                <p className="text-xs text-gray-600">
                  Selected: {studentDisplayName(selectedStudent)}
                  {activeWaitlistStudentIds.has(selectedStudent.id) ? ' (already on waitlist)' : ''}
                </p>
              ) : null}
              {addError ? (
                <p className="text-sm text-red-600 text-balance" role="alert">
                  {addError}
                </p>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {waitlistEntries.length === 0 ? (
            <p className="text-sm text-gray-500">No students on the waitlist.</p>
          ) : (
            <div className="space-y-4">
              {waitlistEntries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-8 h-8 text-sm font-semibold text-white bg-purple-600 rounded-full">
                      #{entry.position}
                    </div>
                    <Avatar
                      fullName={studentDisplayName(entry.student)}
                      cloudinaryPhotoPublicId={entry.student.cloudinaryPhotoPublicId}
                      userId={entry.student.id}
                      size={10}
                    />
                    <div>
                      <p className="font-medium">{studentDisplayName(entry.student)}</p>
                      {hasDistinctStudentLegalName(entry.student) ? (
                        <p className="text-xs text-gray-500">{entry.student.fullName}</p>
                      ) : null}
                      {waitlistSkillLevelLabel(entry.student) ? (
                        <p className="text-sm text-gray-600">{waitlistSkillLevelLabel(entry.student)}</p>
                      ) : null}
                      <p className="text-sm text-gray-500">
                        Added {format(new Date(entry.createdAt), 'MMM d, yyyy')} ({entry.daysWaiting}{' '}
                        {entry.daysWaiting === 1 ? 'day' : 'days'} ago)
                      </p>
                      {entry.notifiedAt && (
                        <p className="text-xs text-orange-600">
                          Notified {format(new Date(entry.notifiedAt), 'MMM d, yyyy h:mm a')}
                          {entry.expired && ' - Expired'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(entry.status)}
                    {entry.status === 'active' && (
                      <Button onClick={() => handlePromote(entry.id)} disabled={actionLoading === entry.id} size="sm">
                        {actionLoading === entry.id ? 'Processing...' : 'Notify'}
                      </Button>
                    )}
                    <Button
                      onClick={() => setRemoveDialogEntryId(entry.id)}
                      disabled={actionLoading === entry.id}
                      variant="outline"
                      size="sm"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmationDialog
        isOpen={Boolean(removeDialogEntryId)}
        onClose={() => setRemoveDialogEntryId(null)}
        onConfirm={handleConfirmRemove}
        title="Remove Student from Waitlist?"
        message="This will remove the student from this class waitlist."
        confirmText="Remove"
        cancelText="Cancel"
        confirmColor="red"
        isLoading={Boolean(removeDialogEntryId) && actionLoading === removeDialogEntryId}
      />
    </>
  )
}

WaitlistManagementSection.propTypes = {
  klassId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  enrolledStudentIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
}

export default WaitlistManagementSection
